import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { queryAPI, exportAPI, connectionsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessage';
import { formatErrorForDisplay } from '../utils/oracleErrorParser';
import { findKeywordTypo, validateCommonSqlErrors } from '../utils/sqlErrorAnalyzer';
import './QueryEditor.css';
import ResultsView from './ResultsView';
import TableBrowser from './TableBrowser';
import SqlAutocomplete from './SqlAutocomplete';
import Prism from 'prismjs';
import 'prismjs/components/prism-sql';
import 'prismjs/themes/prism.css';

function applyQueryError(setError, setParsedError, errMsg, queryToRun, context = {}) {
  const tablesForSuggest = context.tables || [];
  const catalog = (context.autocompleteTables && context.autocompleteTables.length)
    ? context.autocompleteTables
    : tablesForSuggest;
  const columns = context.autocompleteColumns || context.columns || [];
  const formatted = formatErrorForDisplay(errMsg, queryToRun, {
    ...context,
    tables: catalog,
    columns,
  });
  setError(errMsg);
  setParsedError(formatted);
}

const QueryEditor = forwardRef(({ worksheet, selectedConnection, tables = [], packages = [], tablesPrefixCounts, queryMode = 'report', rowLimit: propRowLimit = 500, autocompleteEnabled = true, initialResults = null, selectedPackage = null, showTableBrowser = true, onExecutionStart, onExecutionEnd, initialLoading = false, onRowLimitChange, onResultsChange, onNoConnection, onQueryChange }, ref) => {
  const [autocompleteTables, setAutocompleteTables] = useState([]);
  const [autocompleteColumns, setAutocompleteColumns] = useState([]);

  useEffect(() => {
    setAutocompleteTables(tables);
  }, [tables]);

  const [query, setQuery] = useState(worksheet?.query || '');
  const [results, setResults] = useState(initialResults);
  const [resultsConnectionName, setResultsConnectionName] = useState(initialResults?.connectionName || null);
  const [loading, setLoading] = useState(initialLoading || false);
  const [executionTime, setExecutionTime] = useState(null);
  const [error, setError] = useState(null);
  const [parsedError, setParsedError] = useState(null);
  const [missingTablesCheck, setMissingTablesCheck] = useState(null);
  const [method, setMethod] = useState('rest');
  const [mode, setMode] = useState(queryMode); // sql | hcm_rest | report
  const [sqlEndpointPath, setSqlEndpointPath] = useState(''); // optional custom path or full URL, Postman-style
  const [sizeOfDataChunk] = useState(100); // Default 100, user can expand
  const [rowLimit, setRowLimit] = useState(propRowLimit); // ROWNUM limit for query wrapping
  const [queryHeight, setQueryHeight] = useState(280);
  const [showHcmResourcesModal, setShowHcmResourcesModal] = useState(false);
  const [hcmResources, setHcmResources] = useState([]);
  const [hcmResourcesLoading, setHcmResourcesLoading] = useState(false);
  const [hcmResourcesFromDescribe, setHcmResourcesFromDescribe] = useState(false);
  const [postmanUrl, setPostmanUrl] = useState('');
  const [postmanMethod, setPostmanMethod] = useState('GET');
  const [postmanHeaders, setPostmanHeaders] = useState('');
  const [postmanBody, setPostmanBody] = useState('');
  const [restResponse, setRestResponse] = useState(null);
  const resizeStartY = useRef(null);
  const resizeStartHeight = useRef(null);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // When switching back to a worksheet that has a query running in background, show loading
  useEffect(() => {
    if (initialLoading) setLoading(true);
  }, [worksheet?.id, initialLoading]);

  const handleResizeStart = (e) => {
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = queryHeight;
    const onMouseMove = (e2) => {
      const dy = e2.clientY - resizeStartY.current;
      setQueryHeight(Math.max(150, Math.min(600, resizeStartHeight.current + dy)));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  };

  // Sync mode and rowLimit with props
  useEffect(() => {
    setMode(queryMode);
  }, [queryMode]);
  
  useEffect(() => {
    setRowLimit(propRowLimit);
  }, [propRowLimit]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const autocompleteRef = useRef(null);
  const highlightRef = useRef(null);
  
  // Undo/Redo history
  const [history, setHistory] = useState([worksheet?.query || '']);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Expose current editor content (and selection) for File > Save / Save As
  useImperativeHandle(ref, () => ({
    getContent() {
      const full = query || '';
      const el = textareaRef.current;
      if (!el) return { full, selected: '' };
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = start !== end ? full.slice(start, end) : '';
      return { full, selected };
    },
    undo() {
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setQuery(history[newIndex]);
        if (onQueryChange) {
          onQueryChange(history[newIndex]);
        }
      }
    },
    redo() {
      if (historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setQuery(history[newIndex]);
        if (onQueryChange) {
          onQueryChange(history[newIndex]);
        }
      }
    },
    cut() {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      if (start === end) return;
      
      const before = query.slice(0, start);
      const after = query.slice(end);
      const newQuery = before + after;
      setQuery(newQuery);
      if (onQueryChange) {
        onQueryChange(newQuery);
      }
      
      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newQuery);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      
      // Set cursor position
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start, start);
        setCursorPosition(start);
      }, 0);
    },
    copy() {
      // Copy is handled by Dashboard using getContent()
    },
    paste(text) {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      
      const before = query.slice(0, start);
      const after = query.slice(end);
      const newQuery = before + text + after;
      setQuery(newQuery);
      if (onQueryChange) {
        onQueryChange(newQuery);
      }
      
      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newQuery);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      
      // Set cursor position after pasted text
      const newPosition = start + text.length;
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(newPosition, newPosition);
        setCursorPosition(newPosition);
      }, 0);
    }
  }), [query, history, historyIndex, onQueryChange]);

  // When a package is selected in DB Browser, insert a query for its source text at the cursor
  useEffect(() => {
    if (!selectedPackage) return;
    // Extract bare package name (strip owner prefix like FUSION.PKG_NAME)
    const bareName = String(selectedPackage).split('.').pop();
    if (!bareName) return;
    const pkgName = bareName.replace(/'/g, "''"); // simple quote escaping
    const snippet = `SELECT text\nFROM all_source\nWHERE name = '${pkgName}'\nORDER BY line`;

    // Insert at current cursor position in the editor
    const full = query || '';
    const el = textareaRef.current;
    const pos = el ? el.selectionStart : (cursorPosition || full.length);

    const before = full.slice(0, pos);
    const after = full.slice(pos);

    // Add newlines around snippet when needed
    const needsLeadingNewline = before.length > 0 && !before.endsWith('\n');
    const needsTrailingNewline = after.length > 0 && !after.startsWith('\n');

    const inserted =
      (needsLeadingNewline ? before + '\n' : before) +
      snippet +
      (needsTrailingNewline ? '\n' : '') +
      after;

    setQuery(inserted);
    if (onQueryChange) {
      onQueryChange(inserted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage]); // Only run when selectedPackage changes

  // Sync query from worksheet only when switching worksheets (by id).
  // Do not sync when only initialResults changes, so Clear doesn't get overwritten.
  useEffect(() => {
    if (worksheet) {
      const newQuery = worksheet.query || '';
      setQuery(newQuery);
      // Reset history when switching worksheets
      setHistory([newQuery]);
      setHistoryIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheet?.id]); // Only sync when worksheet ID changes, not the entire worksheet object

  // Restore results only when switching worksheets. Do not sync when initialResults
  // changes on the same worksheet, or we overwrite new execution results with stale data.
  useEffect(() => {
    if (worksheet) {
      if (initialResults && (initialResults.columns || initialResults.data)) {
        const connectionName = initialResults.connectionName || null;
        const cleanResults = { ...initialResults };
        if (cleanResults.connectionName) {
          delete cleanResults.connectionName;
        }
        setResults(cleanResults);
        setResultsConnectionName(connectionName);
        console.log('Restored results for worksheet:', worksheet.id, 'Connection:', connectionName);
      } else {
        setResults(null);
        setResultsConnectionName(null);
      }
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheet?.id]); // intentionally not initialResults: avoid overwriting after run

  // Notify parent when results change (e.g. from restore when switching tabs)
  useEffect(() => {
    if (onResultsChange && worksheet?.id && results) {
      const resultsWithConnection = {
        ...results,
        connectionName: resultsConnectionName
      };
      onResultsChange(worksheet.id, resultsWithConnection, resultsConnectionName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, resultsConnectionName, worksheet?.id]); // onResultsChange is stable from parent

  // Get the query to execute: selected text if any, otherwise the statement at cursor (between semicolons)
  const getQueryToExecute = () => {
    const el = textareaRef.current;
    const full = query;
    if (!el && full.length === 0) return '';
    const start = el ? el.selectionStart : cursorPosition;
    const end = el ? el.selectionEnd : cursorPosition;
    if (start !== end) {
      return full.slice(start, end).trim();
    }
    const lastSemi = full.lastIndexOf(';', start);
    const nextSemi = full.indexOf(';', start);
    const from = lastSemi === -1 ? 0 : lastSemi + 1;
    const to = nextSemi === -1 ? full.length : nextSemi;
    return full.slice(from, to).trim();
  };

  const wasQueryWrappedWithRownum = (q, limit) => {
    if (!q?.trim() || !limit || parseInt(limit) <= 0) return false;
    const clean = q.trim().replace(/;/g, '');
    const upper = clean.toUpperCase();
    return !upper.includes('ROWNUM') && !upper.includes('FETCH FIRST') && !upper.includes('FETCH NEXT') && !upper.includes('LIMIT');
  };

  const handleExecute = async () => {
    if (!selectedConnection && mode !== 'postman') {
      onNoConnection();
      return;
    }

    const wsId = worksheet?.id;

    if (mode === 'postman') {
      const urlTrim = (postmanUrl || '').trim();
      if (!urlTrim) {
        toast.error('Enter a URL or path');
        return;
      }
      const isFullUrl = /^https?:\/\//i.test(urlTrim);
      if (!isFullUrl && !selectedConnection) {
        toast.error('Select a connection (for base URL and auth) or enter a full URL');
        return;
      }
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      if (onExecutionStart && wsId) onExecutionStart(wsId);
      setLoading(true);
      setError(null);
      setRestResponse(null);
      setResults(null);
      let parsedHeaders = {};
      if ((postmanHeaders || '').trim()) {
        const raw = postmanHeaders.trim();
        try {
          parsedHeaders = JSON.parse(raw);
          if (typeof parsedHeaders !== 'object' || Array.isArray(parsedHeaders)) parsedHeaders = {};
        } catch {
          raw.split(/\r?\n/).forEach(line => {
            const idx = line.indexOf(':');
            if (idx > 0) {
              const k = line.slice(0, idx).trim();
              const v = line.slice(idx + 1).trim();
              if (k) parsedHeaders[k] = v;
            }
          });
        }
      }
      let bodyPayload = undefined;
      if ((postmanBody || '').trim() && !['GET', 'HEAD'].includes(postmanMethod.toUpperCase())) {
        const raw = postmanBody.trim();
        try {
          bodyPayload = JSON.parse(raw);
        } catch {
          bodyPayload = raw;
        }
      }
      try {
        const response = await queryAPI.restRequest(
          selectedConnection?.id || null,
          urlTrim,
          postmanMethod,
          parsedHeaders,
          bodyPayload,
          { signal: controller.signal }
        );
        if (response.data.success) {
          setRestResponse({
            status: response.data.status,
            statusText: response.data.statusText,
            headers: response.data.headers || {},
            data: response.data.data
          });
          setError(null);
        } else {
          setError(response.data?.error || 'Request failed');
          setParsedError(null);
          setRestResponse(null);
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          toast.info('Request cancelled');
        } else {
          setError(getErrorMessage(err, 'Request failed'));
          setParsedError(null);
          setRestResponse(null);
        }
      } finally {
        abortControllerRef.current = null;
        if (onExecutionEnd && wsId) onExecutionEnd(wsId);
        if (mountedRef.current) setLoading(false);
      }
      return;
    }

    const queryToRun = getQueryToExecute();
    if (!queryToRun) {
      toast.error('Place cursor in a query or select text to execute');
      return;
    }

    // Pre-check: AND without WHERE is a common mistake; Oracle often reports it as ORA-00907
    if (mode === 'report' || mode === 'sql') {
      const qUpper = queryToRun.toUpperCase().trim();
      const qTrim = queryToRun.trim();

      // 1) Catch misspelled keywords (FORM, FRO, SELEC, SELCT, etc.)
      const typo = findKeywordTypo(queryToRun);
      if (typo) {
        const errMsg = `Local check: Did you mean "${typo.suggestion}" instead of "${typo.typo}"?`;
        applyQueryError(setError, setParsedError, errMsg, queryToRun, {
          tables,
          autocompleteTables,
          autocompleteColumns,
          wrapped: false,
          diagnostic: {
            source: 'local',
            errorCode: 'SQL Syntax',
            message: `Did you mean "${typo.suggestion}" instead of "${typo.typo}"?`,
            fixes: [`Replace ${typo.typo} with ${typo.suggestion}`],
            rangeConfidence: 'none',
          },
        });
        setResults(null);
        return;
      }

      // 2) AND without WHERE
      if (/\bFROM\b/.test(qUpper) && /\bAND\b/.test(qUpper) && !/\bWHERE\b/.test(qUpper)) {
        const errMsg = 'Local check: WHERE clause is missing before AND conditions.';
        applyQueryError(setError, setParsedError, errMsg, queryToRun, {
          tables,
          autocompleteTables,
          autocompleteColumns,
          wrapped: false,
          diagnostic: {
            source: 'local',
            errorCode: 'SQL Syntax',
            message: 'WHERE clause is missing. Use WHERE before filter conditions (not AND alone after FROM).',
            fixes: ["Example: SELECT * FROM my_table WHERE language = 'US'"],
            rangeConfidence: 'none',
          },
        });
        setResults(null);
        return;
      }

      // 3) SELECT-like query but no FROM clause (missing or completely wrong spelling)
      const looksLikeSelect = /^\s*(SELECT|SELEC|SELET|SELCT|SLECT|SELCET)\b/i.test(qTrim) || /^\s*\*\s+/i.test(qTrim);
      const hasFrom = /\bFROM\b/.test(qUpper);
      if (looksLikeSelect && !hasFrom) {
        const errMsg = 'Local check: FROM clause is missing.';
        applyQueryError(setError, setParsedError, errMsg, queryToRun, {
          tables,
          autocompleteTables,
          autocompleteColumns,
          wrapped: false,
          diagnostic: {
            source: 'local',
            errorCode: 'SQL Syntax',
            message: 'A SELECT statement must include FROM followed by a table or view name.',
            fixes: ['Example: SELECT * FROM my_table'],
            rangeConfidence: 'none',
          },
        });
        setResults(null);
        return;
      }

      // 4) Other common SQL errors (parentheses, strings, commas, etc.) â€” hard local checks only
      const commonError = validateCommonSqlErrors(queryToRun);
      if (commonError) {
        applyQueryError(setError, setParsedError, `Local check: ${commonError.error}`, queryToRun, {
          tables,
          autocompleteTables,
          autocompleteColumns,
          wrapped: false,
          diagnostic: {
            source: 'local',
            errorCode: 'SQL Syntax',
            message: commonError.error,
            objectName: commonError.nextTable || null,
            objectType: commonError.nextTable ? 'table' : null,
            fixes: commonError.suggestion ? [commonError.suggestion] : [],
            range: commonError.line
              ? { line: commonError.line, column: commonError.column || 1 }
              : null,
            rangeConfidence: commonError.line ? 'high' : 'none',
          },
        });
        setResults(null);
        return;
      }
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    if (onExecutionStart && wsId) onExecutionStart(wsId);
    setLoading(true);
    setError(null);
    setParsedError(null);
    setMissingTablesCheck(null);
    setResults(null);

    const wrapped = wasQueryWrappedWithRownum(queryToRun, rowLimit);

    try {
      let queryResults = null;

      if (mode === 'report') {
        const body = {
          sizeOfDataChunkDownload: sizeOfDataChunk,
        };
        
        // Pass row_limit to backend
        if (rowLimit && parseInt(rowLimit) > 0) {
          body.row_limit = parseInt(rowLimit);
        }
        
        // Only include query if provided (run report without parameters if empty)
        if (queryToRun.trim()) {
          let queryClean = queryToRun.trim().replace(/;/g, '');
          
          // Pre-check: validate syntax BEFORE wrapping (wrapped SELECT * FROM (inner) hides inner errors)
          try {
            const validateRes = await queryAPI.validate(queryClean, { signal });
            if (validateRes.data && validateRes.data.valid === false) {
              const v = validateRes.data;
              const errMsg = v.error ? `Local check: ${v.error}` : 'Local check: SQL syntax error';
              applyQueryError(setError, setParsedError, errMsg, queryToRun, {
                tables,
                autocompleteTables,
                autocompleteColumns,
                wrapped,
                diagnostic: v.diagnostic || {
                  source: 'local',
                  errorCode: 'SQL Syntax',
                  message: v.error || 'SQL syntax error',
                  range: v.line ? { line: v.line, column: v.column || 1 } : null,
                  rangeConfidence: v.line ? 'high' : 'none',
                  fixes: v.suggestion ? [v.suggestion] : [],
                },
              });
              setResults(null);
              return;
            }
          } catch (validateErr) {
            if (validateErr.response?.status === 400 && validateErr.response?.data) {
              const v = validateErr.response.data;
              const errMsg = v.error || 'Local check: SQL syntax error';
              applyQueryError(setError, setParsedError, errMsg, queryToRun, {
                tables,
                autocompleteTables,
                autocompleteColumns,
                wrapped,
                diagnostic: v.diagnostic || {
                  source: 'local',
                  errorCode: 'SQL Syntax',
                  message: v.validationError?.error || v.error || 'SQL syntax error',
                  range: v.validationError?.line
                    ? { line: v.validationError.line, column: v.validationError.column || 1 }
                    : null,
                  rangeConfidence: v.validationError?.line ? 'high' : 'none',
                  fixes: v.validationError?.suggestion ? [v.validationError.suggestion] : [],
                },
              });
              setResults(null);
              return;
            }
          }
          
          // Apply row limit by wrapping query with ROWNUM (similar to SQL mode)
          if (rowLimit && parseInt(rowLimit) > 0) {
            const limit = parseInt(rowLimit);
            const queryUpper = queryClean.toUpperCase();
            const hasRownum = queryUpper.includes('ROWNUM');
            const hasFetchFirst = queryUpper.includes('FETCH FIRST') || queryUpper.includes('FETCH NEXT');
            const hasLimit = queryUpper.includes('LIMIT');
            
            if (!hasRownum && !hasFetchFirst && !hasLimit) {
              // Wrap query with ROWNUM <= limit
              queryClean = `SELECT * FROM (${queryClean}) WHERE ROWNUM <= ${limit}`;
              console.log(`[REPORT] Applied row limit ${limit}, wrapped query: ${queryClean.substring(0, 100)}...`);
            } else {
              console.log(`[REPORT] Row limit ${limit} requested but query already has ROWNUM/FETCH FIRST/LIMIT, skipping wrapper`);
            }
          }

          body.query = btoa(unescape(encodeURIComponent(queryClean)));
        }
        const response = await connectionsAPI.runReport(selectedConnection.id, body, { signal });
        console.log('Report API Response:', response.data);
        
        if (response.data.success) {
          setExecutionTime(null);
          
          // Check for columns/data format (from convertXmlToTableData)
          if (response.data.columns && response.data.data) {
            queryResults = {
              columns: response.data.columns,
              data: response.data.data,
              rowCount: response.data.rowCount || (response.data.data || []).length,
            };
            console.log('Setting results with columns/data:', queryResults);
            console.log('Connection name:', selectedConnection?.name);
            setResults(queryResults);
            const connName = selectedConnection?.name || null;
            setResultsConnectionName(connName);
            console.log('Set resultsConnectionName to:', connName);
            if (onResultsChange && wsId) onResultsChange(wsId, { ...queryResults, connectionName: connName }, connName);
          } else if (response.data.report_xml) {
            // Fallback: if report_xml is returned, display it
            queryResults = {
              columns: ['Report output'],
              data: [[response.data.report_xml.substring(0, 50000)]],
              rowCount: 1,
            };
            setResults(queryResults);
            setResultsConnectionName(selectedConnection?.name || null);
            if (onResultsChange && wsId) onResultsChange(wsId, { ...queryResults, connectionName: selectedConnection?.name }, selectedConnection?.name);
          } else {
            // Log what we received for debugging
            console.log('Report response structure:', {
              hasColumns: !!response.data.columns,
              hasData: !!response.data.data,
              hasRowCount: !!response.data.rowCount,
              keys: Object.keys(response.data),
              fullResponse: response.data
            });
            // No toast - just show empty results
          }
        } else {
          const msg = (response.data?.error || '').trim() || 'Report execution failed. Check the report path and query parameters.';
          applyQueryError(setError, setParsedError, msg, queryToRun, {
            tables,
            autocompleteTables,
            autocompleteColumns,
            wrapped,
            diagnostic: response.data?.diagnostic,
          });
          setResults(null);
          if (onResultsChange && wsId) onResultsChange(wsId, null, null);
        }
      } else {
        // Pre-check: validate syntax before sending (SQL mode)
        if (mode === 'sql' && queryToRun.trim()) {
          try {
            const validateRes = await queryAPI.validate(queryToRun, { signal });
            if (validateRes.data && validateRes.data.valid === false) {
              const v = validateRes.data;
              applyQueryError(setError, setParsedError, v.error || 'Local check: SQL syntax error', queryToRun, {
                tables,
                autocompleteTables,
                autocompleteColumns,
                wrapped,
                diagnostic: v.diagnostic || {
                  source: 'local',
                  errorCode: 'SQL Syntax',
                  message: v.error || 'SQL syntax error',
                  range: v.line ? { line: v.line, column: v.column || 1 } : null,
                  rangeConfidence: v.line ? 'high' : 'none',
                  fixes: v.suggestion ? [v.suggestion] : [],
                },
              });
              setResults(null);
              return;
            }
          } catch (validateErr) {
            if (validateErr.response?.status === 400 && validateErr.response?.data) {
              const v = validateErr.response.data;
              applyQueryError(setError, setParsedError, v.error || 'Local check: SQL syntax error', queryToRun, {
                tables,
                autocompleteTables,
                autocompleteColumns,
                wrapped,
                diagnostic: v.diagnostic,
              });
              setResults(null);
              return;
            }
          }
        }
        const response = await queryAPI.execute(
          selectedConnection.id,
          queryToRun,
          method,
          mode,
          { signal, sqlEndpointPath: sqlEndpointPath.trim() || undefined },
          rowLimit
        );
        if (response.data.success) {
          queryResults = {
            columns: response.data.columns,
            data: response.data.data,
            rowCount: response.data.row_count,
          };
          setResults(queryResults);
          setResultsConnectionName(selectedConnection?.name || null);
          setExecutionTime(response.data.execution_time);
          if (onResultsChange && wsId) onResultsChange(wsId, { ...queryResults, connectionName: selectedConnection?.name }, selectedConnection?.name);
        } else {
          let msg = (response.data?.error || '').trim() || 'Query failed';
          applyQueryError(setError, setParsedError, msg, queryToRun, {
            tables,
            autocompleteTables,
            autocompleteColumns,
            wrapped,
            diagnostic: response.data?.diagnostic,
          });
          setResults(null);
          if (onResultsChange && wsId) onResultsChange(wsId, null, null);
        }
      }
      
      // Results will be displayed in the results section below
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError' || (err.code === 'ERR_CANCELED')) {
        toast.info('Query cancelled');
        setError(null);
      } else {
        const data = err.response?.data;
        let errorMsg = getErrorMessage(err, 'Execution failed');
        if ((errorMsg === 'Network Error' || errorMsg === 'Failed to fetch') && !data) {
          errorMsg = 'Request failed. This may be a network issue, or the server may have timed out. If your query has an error, try simplifying it or check your connection.';
        }
        applyQueryError(setError, setParsedError, errorMsg, queryToRun, {
          tables,
          autocompleteTables,
          autocompleteColumns,
          wrapped,
          diagnostic: data?.diagnostic,
        });
        setResults(null);
        if (onResultsChange && wsId) onResultsChange(wsId, null, null);
      }
    } finally {
      abortControllerRef.current = null;
      if (onExecutionEnd && wsId) onExecutionEnd(wsId);
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleOpenHcmResources = async () => {
    if (!selectedConnection?.id) {
      toast.warning('Select a connection first');
      return;
    }
    setShowHcmResourcesModal(true);
    setHcmResourcesLoading(true);
    setHcmResources([]);
    try {
      const res = await queryAPI.getHcmRestResources(selectedConnection.id);
      const list = res.data?.resources || [];
      setHcmResources(Array.isArray(list) ? list : []);
      setHcmResourcesFromDescribe(!!res.data?.fromDescribe);
    } catch {
      setHcmResources([]);
      setHcmResourcesFromDescribe(false);
    } finally {
      setHcmResourcesLoading(false);
    }
  };

  const handleSelectHcmResource = (path) => {
    if (path && onQueryChange) {
      const newQuery = (query || '').trim() ? query + '\n' + path : path;
      setQuery(newQuery);
      onQueryChange(newQuery);
    }
    setShowHcmResourcesModal(false);
  };

  const handleFormat = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query to format');
      return;
    }

    try {
      console.log('Formatting query:', query.substring(0, 50) + '...');
      const response = await queryAPI.format(query);
      console.log('Format response:', response.data);
      const formattedQuery = response.data.formatted_query || query;
      
      if (formattedQuery === query) {
        console.warn('Formatted query is same as original - backend may not be formatting');
      }
      
      handleQueryChange(formattedQuery, true); // Add formatted query to history
      // Focus the textarea and move cursor to end after formatting
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const length = formattedQuery.length;
          textareaRef.current.setSelectionRange(length, length);
          setCursorPosition(length);
        }
      }, 0);
      toast.success('Query formatted');
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to format query');
      toast.error(errorMsg);
      console.error('Format query error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
    }
  };

  const handleExport = async (format) => {
    if (!results) {
      toast.error('No results to export');
      return;
    }

    if (!results.columns || !results.data) {
      toast.error('Invalid results data');
      return;
    }

    try {
      // Generate filename with connection name + date stamp
      const connectionName = selectedConnection?.name || 'export';
      // Sanitize connection name for filename (remove invalid characters)
      const sanitizedName = connectionName.replace(/[^a-zA-Z0-9_-]/g, '_');
      // Format date as YYYYMMDD_HHMMSS
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const dateStamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
      const filename = `${sanitizedName}_${dateStamp}`;
      
      await exportAPI.exportData(format, results.columns, results.data, filename);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      const errorMsg = err.message || 'Export failed';
      console.error('Export error:', err);
      toast.error(errorMsg);
    }
  };

  const handleClear = () => {
    setResults(null);
    setResultsConnectionName(null);
    setError(null);
    setParsedError(null);
    setRestResponse(null);
    setExecutionTime(null);
    if (onResultsChange && worksheet?.id) onResultsChange(worksheet.id, null, null);
  };

  const handleTableSelect = (tableName) => {
    // Insert table name at cursor position
    const el = textareaRef.current;
    if (!el) return;
    
    const currentQuery = query || '';
    const start = el.selectionStart;
    const end = el.selectionEnd;
    
    // Insert table name at cursor position
    const before = currentQuery.slice(0, start);
    const after = currentQuery.slice(end);
    const newQuery = before + tableName + after;
    
    setQuery(newQuery);
    if (onQueryChange) {
      onQueryChange(newQuery);
    }
    
    // Set cursor position after inserted text
    const newPosition = start + tableName.length;
    setCursorPosition(newPosition);
    setTimeout(() => {
      if (el) {
        el.setSelectionRange(newPosition, newPosition);
        el.focus();
      }
    }, 0);
  };

  const handleFindMissingTable = async () => {
    const tablesInQuery = parsedError?.sqlAnalysis?.tablesInQuery;
    if (!selectedConnection || !tablesInQuery?.length) return;
    setMissingTablesCheck({ loading: true });
    try {
      const res = await queryAPI.checkTables(selectedConnection.id, tablesInQuery);
      const data = res?.data || {};
      setMissingTablesCheck({
        loading: false,
        missing: data.missing || [],
        existing: data.existing || [],
        error: data.error
      });
    } catch (e) {
      setMissingTablesCheck({
        loading: false,
        error: getErrorMessage(e)
      });
    }
  };

  const handleQueryChange = (newQuery, addToHistory = false) => {
    setQuery(newQuery);
    if (onQueryChange) {
      onQueryChange(newQuery);
    }
    
    // Add to history for undo/redo if requested
    if (addToHistory) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newQuery);
      // Limit history to 50 entries
      if (newHistory.length > 50) {
        newHistory.shift();
      } else {
        setHistoryIndex(newHistory.length - 1);
      }
      setHistory(newHistory);
    }
  };

  const handleTextareaChange = (e) => {
    const newQuery = e.target.value;
    handleQueryChange(newQuery, true); // Add to history
    setCursorPosition(e.target.selectionStart);
  };

  const handleTextareaKeyDown = (e) => {
    // Let autocomplete handle its own keys first
    if (autocompleteRef.current && autocompleteRef.current.handleKeyDown) {
      const handled = autocompleteRef.current.handleKeyDown(e);
      if (handled) {
        return;
      }
    }

    // Dismiss autocomplete on Escape key
    if (e.key === 'Escape' && autocompleteRef.current && autocompleteRef.current.dismiss) {
      autocompleteRef.current.dismiss();
    }

    // Track cursor position on arrow keys and other navigation
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
      setTimeout(() => {
        if (textareaRef.current) {
          setCursorPosition(textareaRef.current.selectionStart);
        }
      }, 0);
    }
  };

  const handleTextareaClick = (e) => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  };

  const handleTextareaSelect = (e) => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  };

  // Syntax highlighting effect for Oracle SQL (sql and report modes both use SQL)
  useEffect(() => {
    const highlightModes = ['sql', 'report'];
    if (!highlightModes.includes(mode) || !highlightRef.current) {
      if (highlightRef.current) {
        highlightRef.current.innerHTML = '';
      }
      return;
    }

    // Highlight SQL using Prism (handles empty strings gracefully)
    const sqlCode = query || '';
    try {
      let highlighted = Prism.highlight(sqlCode, Prism.languages.sql, 'sql');
      // Give AND/AS a distinct color (Prism: AS=keyword, AND=operator)
      highlighted = highlighted.replace(
        /<span class="token keyword">(as)<\/span>/gi,
        '<span class="token keyword keyword-connector">$1</span>'
      );
      highlighted = highlighted.replace(
        /<span class="token operator">(and|or|not)<\/span>/gi,
        '<span class="token operator keyword-connector">$1</span>'
      );
      highlightRef.current.innerHTML = highlighted;
    } catch (error) {
      // Fallback: escape HTML if highlighting fails
      const div = document.createElement('div');
      div.textContent = sqlCode;
      highlightRef.current.innerHTML = div.innerHTML;
    }

    // Sync scroll position
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, [query, mode]);

  // Sync scroll on scroll event
  const handleTextareaScroll = (e) => {
    // Sync scroll with line numbers
    const lineNumbers = e.target.parentElement.querySelector('.query-textarea-line-numbers');
    if (lineNumbers) {
      lineNumbers.scrollTop = e.target.scrollTop;
    }
    // Sync scroll with syntax highlight overlay
    if (highlightRef.current) {
      highlightRef.current.scrollTop = e.target.scrollTop;
      highlightRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  return (
    <div className={`query-editor ${showTableBrowser ? 'query-editor-with-browser' : ''}`}>
      <div className="editor-main">
        <div className="editor-section">
      {/* Query Section - resizable height */}
      <div className="query-section" style={{ height: queryHeight }}>
        <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {mode === 'postman' ? (
            <div className="postman-request-form">
              <div className="postman-row postman-url-row">
                <select
                  className="postman-method-select"
                  value={postmanMethod}
                  onChange={(e) => setPostmanMethod(e.target.value)}
                  title="HTTP method"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                  <option value="HEAD">HEAD</option>
                </select>
                <input
                  type="text"
                  className="postman-url-input"
                  placeholder="URL or path (e.g. https://api.example.com/foo or /hcmRestApi/resources/11.13.18.05/emps)"
                  value={postmanUrl}
                  onChange={(e) => setPostmanUrl(e.target.value)}
                  title="Full URL or path relative to connection base URL"
                />
              </div>
              <div className="postman-row">
                <label className="postman-label">Headers (optional)</label>
                <textarea
                  className="postman-headers-input"
                  placeholder={'Content-Type: application/json\nAuthorization: Bearer <token>\nOr JSON: {"X-Custom": "value"}'}
                  value={postmanHeaders}
                  onChange={(e) => setPostmanHeaders(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="postman-row">
                <label className="postman-label">Body (optional, for POST/PUT/PATCH)</label>
                <textarea
                  className="postman-body-input"
                  placeholder='JSON or raw text'
                  value={postmanBody}
                  onChange={(e) => setPostmanBody(e.target.value)}
                  rows={6}
                  disabled={['GET', 'HEAD'].includes(postmanMethod)}
                />
              </div>
            </div>
          ) : (
          <>
          <div className="query-textarea-wrapper">
            <div className="query-textarea-line-numbers">
              {query.split('\n').map((_, index) => (
                <div key={index}>{index + 1}</div>
              ))}
            </div>
            {(mode === 'sql' || mode === 'report') && (
              <pre
                ref={highlightRef}
                className="query-textarea-highlight"
                aria-hidden="true"
              />
            )}
            <textarea
              ref={textareaRef}
              className={`query-textarea ${(mode === 'sql' || mode === 'report') ? 'query-textarea-with-highlight' : ''}`}
              placeholder={
                mode === 'sql'
                  ? 'Enter your SQL query...\n\nExample:\nSELECT * FROM workers WHERE status = \'A\''
                  : mode === 'hcm_rest'
                  ? 'HCM REST resource path (relative to /hcmRestApi/resources/...)\n\nExamples:\n  emps\n  emps?limit=100\n  absences\n  workersLov?limit=50\n  perPersonEmails?q=PersonNumber=12345'
                  : 'Enter your report query'
              }
              value={query}
              onChange={handleTextareaChange}
              onKeyDown={handleTextareaKeyDown}
              onClick={handleTextareaClick}
              onSelect={handleTextareaSelect}
              onScroll={handleTextareaScroll}
              disabled={loading}
            />
          </div>
          {selectedConnection && autocompleteEnabled && (
            <SqlAutocomplete
              ref={autocompleteRef}
              query={query}
              onQueryChange={handleQueryChange}
              tables={autocompleteTables}
              packages={packages}
              columns={autocompleteColumns}
              cursorPosition={cursorPosition}
              textareaRef={textareaRef}
            />
          )}
          </>
          )}
        </div>

        {executionTime !== null && (
          <div className="execution-info">
            Execution time: {executionTime.toFixed(3)}s
          </div>
        )}
      </div>

      {/* Resize handle - drag to change query box height */}
      <div
        className="query-resize-handle"
        onMouseDown={handleResizeStart}
        title="Drag to resize query box"
      />

      {/* Action Buttons - Between Query and Results */}
      <div className="action-buttons-bar">
        <div className="action-buttons-left">
          {mode === 'sql' && (
            <>
              <div className="method-selector">
                <label>Method:</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}>
                  <option value="rest">POST</option>
                  <option value="get">GET</option>
                </select>
              </div>
              <div className="sql-endpoint-input-wrap">
                <label title="Override the SQL endpoint path or use a full URL (like Postman)">Endpoint:</label>
                <input
                  type="text"
                  className="sql-endpoint-input"
                  placeholder="/rest/sql or full URL"
                  value={sqlEndpointPath}
                  onChange={(e) => setSqlEndpointPath(e.target.value)}
                  title="Optional. Path (e.g. /rest/sql, /ords/sql) or full URL. Leave blank to use server default."
                />
              </div>
            </>
          )}
          {mode === 'hcm_rest' && selectedConnection && (
            <button
              type="button"
              className="btn-browse-hcm-resources"
              onClick={handleOpenHcmResources}
              title="Show available HCM REST resource paths"
            >
              Browse resources
            </button>
          )}
          <div className="editor-toolbar">
            <div className="execute-clear-group">
              <button
                className={`btn-execute ${loading ? 'executing' : ''}`}
                onClick={handleExecute}
                disabled={loading || (mode === 'postman' ? !(postmanUrl || '').trim() : !selectedConnection)}
                title={mode === 'postman' ? 'Send request' : 'Execute the query at cursor (or selected text)'}
              >
                {loading ? <span className="btn-execute-loading">Executingâ€¦</span> : <span className="btn-execute-label">Run</span>}
              </button>
              <button
                className={loading ? 'btn-cancel' : 'btn-clear'}
                onClick={loading ? handleCancel : handleClear}
                title={loading ? 'Cancel query' : 'Clear results'}
              >
                <span className="btn-clear-icon">âœ•</span>
              </button>
            </div>
            <button
              className="btn-format"
              onClick={handleFormat}
              disabled={loading || !query.trim() || mode === 'postman'}
              title="Format SQL query"
            >
              Format SQL
            </button>
            {results && resultsConnectionName ? (
              <div className="results-connection-badge-inline" title={`Results from connection: ${resultsConnectionName}`}>
                <span className="results-connection-label">Results from:</span>
                <span className="results-connection-name">{resultsConnectionName}</span>
              </div>
            ) : null}
            <button
              className="btn-export csv"
              onClick={() => handleExport('csv')}
              disabled={!results || loading}
              title={results ? "Export to CSV" : "Execute a query to export results"}
            >
              CSV
            </button>
            <button
              className="btn-export xls"
              onClick={() => handleExport('xls')}
              disabled={!results || loading}
              title={results ? "Export to Excel" : "Execute a query to export results"}
            >
              Excel
            </button>
            <button
              className="btn-export hdl"
              onClick={() => handleExport('dat')}
              disabled={!results || loading}
              title={results ? "Export to .dat" : "Execute a query to export results"}
            >
              .dat
            </button>
            <button
              className="btn-export pdf"
              onClick={() => handleExport('pdf')}
              disabled={!results || loading}
              title={results ? "Export to PDF" : "Execute a query to export results"}
            >
              PDF
            </button>
          </div>
        </div>
        <div className="action-buttons-right">
          {(mode === 'report' || mode === 'sql' || mode === 'hcm_rest') && (
            <div className="row-limit-selector-inline">
              <label>Max rows:</label>
              <input
                type="number"
                min="1"
                max="25000"
                placeholder="e.g. 500"
                value={rowLimit}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  const newLimit = (val > 0 && val <= 25000) ? val : (rowLimit || 500);
                  setRowLimit(newLimit);
                  if (onRowLimitChange) {
                    onRowLimitChange(newLimit);
                  }
                }}
                style={{ width: '80px', marginLeft: '8px' }}
                title={mode === 'hcm_rest' ? 'Limit passed to HCM REST as ?limit=N (Oracle may cap at 1000)' : mode === 'sql' ? 'Wrap query with ROWNUM to limit rows' : 'Limit rows returned by report'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Results Section - Lower Part */}
      <div className="results-section">
        {restResponse && (
          <div className="rest-response-panel">
            <div className="rest-response-header">
              <span className="rest-response-status" data-status-type={restResponse.status >= 200 && restResponse.status < 300 ? 'success' : restResponse.status >= 400 ? 'error' : 'info'}>
                {restResponse.status} {restResponse.statusText}
              </span>
            </div>
            <details className="rest-response-headers-details">
              <summary>Response headers</summary>
              <pre className="rest-response-headers">
                {Object.entries(restResponse.headers || {}).map(([k, v]) => `${k}: ${v}`).join('\n') || '(none)'}
              </pre>
            </details>
            <div className="rest-response-body">
              <label>Response body</label>
              <pre className="rest-response-body-content">
                {typeof restResponse.data === 'object' && restResponse.data !== null
                  ? JSON.stringify(restResponse.data, null, 2)
                  : String(restResponse.data ?? '')}
              </pre>
            </div>
          </div>
        )}
        {error ? (
          <div className="error-panel" role="alert">
            <div className="error-panel-header">
              <h3>
                <span className={`error-source-badge error-source-${parsedError?.source || 'oracle'}`}>
                  {parsedError?.sourceLabel || 'Error'}
                </span>
                {parsedError?.errorCode ? ` ${parsedError.errorCode}` : ''}
              </h3>
              <button
                type="button"
                className="error-panel-close"
                onClick={() => {
                  setError(null);
                  setParsedError(null);
                  setMissingTablesCheck(null);
                }}
                title="Close error"
                aria-label="Close error"
              >
                âœ•
              </button>
            </div>
            <div className="error-panel-content">
              <div className="error-message">
                <pre>{parsedError?.formatted || error}</pre>
              </div>
              {parsedError?.objectName && (
                <div className="error-object">
                  <strong>In your SQL:</strong>{' '}
                  <code>{parsedError.objectName}</code>
                  {parsedError.objectType ? ` (${parsedError.objectType})` : ''}
                </div>
              )}
              {parsedError?.highlightedLine && (
                <div className="error-line-info">
                  <div className="error-line-label">
                    Line {parsedError.highlightedLine.lineNumber}
                    {parsedError.highlightedLine.column ? `, column ${parsedError.highlightedLine.column}` : ''}:
                  </div>
                  <div className="error-line-code">
                    <code>{parsedError.highlightedLine.content}</code>
                  </div>
                  {parsedError.highlightedLine.column && parsedError.highlightedLine.content && (
                    <div className="error-line-caret">
                      {' '.repeat(Math.min(parsedError.highlightedLine.column - 1, parsedError.highlightedLine.content.length))}^
                    </div>
                  )}
                </div>
              )}
              {parsedError?.sqlAnalysis?.plainExplanation && (
                <div className="error-plain-explanation">
                  <strong>What went wrong:</strong> {parsedError.sqlAnalysis.plainExplanation}
                </div>
              )}
              {parsedError?.sqlAnalysis?.didYouMean && parsedError.sqlAnalysis.didYouMean.length > 0 && (
                <div className="error-did-you-mean">
                  <strong>Did you mean:</strong>{' '}
                  {parsedError.sqlAnalysis.didYouMean.map((t, i) => (
                    <span key={t}>
                      <code className="suggested-table">{t}</code>
                      {i < parsedError.sqlAnalysis.didYouMean.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                  ?
                </div>
              )}
              {parsedError?.sqlAnalysis?.typoSuggestion && (
                <div className="error-typo-suggestion">
                  {parsedError.sqlAnalysis.typoSuggestion}
                </div>
              )}
              {(parsedError?.suggestions?.length > 0 || parsedError?.sqlAnalysis?.fixes?.length > 0) && (
                <div className="error-try-this">
                  <strong>Try this:</strong>
                  <ul>
                    {(parsedError.sqlAnalysis?.fixes || parsedError.suggestions || []).slice(0, 3).map((fix) => (
                      <li key={fix}>{fix}</li>
                    ))}
                  </ul>
                </div>
              )}
              {parsedError?.errorCode === 'ORA-00942' && parsedError?.sqlAnalysis?.tablesInQuery?.length > 1 && (
                <div className="error-find-missing">
                  <button
                    type="button"
                    className="find-missing-table-btn"
                    onClick={handleFindMissingTable}
                    disabled={missingTablesCheck?.loading || !selectedConnection}
                  >
                    {missingTablesCheck?.loading ? 'Checkingâ€¦' : 'Find which table'}
                  </button>
                  {missingTablesCheck && !missingTablesCheck.loading && (
                    <div className="missing-tables-result">
                      {missingTablesCheck.error ? (
                        <span className="missing-tables-error">{missingTablesCheck.error}</span>
                      ) : missingTablesCheck.missing?.length > 0 ? (
                        <>
                          <strong>Table(s) that do not exist:</strong>{' '}
                          {missingTablesCheck.missing.map((t, i) => (
                            <span key={t}>
                              <code>{t}</code>
                              {i < missingTablesCheck.missing.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </>
                      ) : (
                        <span>All tables in your query exist. The error may be due to privileges or schema.</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {parsedError?.primaryError && parsedError.primaryError !== parsedError.formatted && (
                <details className="error-technical-details">
                  <summary>Technical details</summary>
                  <pre>{parsedError.primaryError}</pre>
                </details>
              )}
            </div>
          </div>
        ) : results ? (
          <ResultsView
            results={results}
          />
        ) : (
          <div className="no-results-message">
            <p>Execute a query to see results here</p>
          </div>
        )}
      </div>
        </div>

        {showTableBrowser && (
        <div className="browser-section">
          <TableBrowser
            selectedConnection={selectedConnection}
            tables={tables}
            tablesPrefixCounts={tablesPrefixCounts}
            onTableSelect={handleTableSelect}
            onTablesChange={setAutocompleteTables}
            onColumnsLoaded={setAutocompleteColumns}
          />
        </div>
        )}
      </div>

      {showHcmResourcesModal && (
        <div className="hcm-resources-modal-overlay" onClick={() => setShowHcmResourcesModal(false)}>
          <div className="hcm-resources-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hcm-resources-modal-header">
              <h4>HCM REST resources</h4>
              <button type="button" className="hcm-resources-modal-close" onClick={() => setShowHcmResourcesModal(false)} title="Close">âœ•</button>
            </div>
            <div className="hcm-resources-modal-body">
              {hcmResourcesLoading ? (
                <p>Loading resourcesâ€¦</p>
              ) : hcmResources.length === 0 ? (
                <p>No resources found. Try using examples from the placeholder.</p>
              ) : (
                <>
                  {hcmResourcesFromDescribe ? (
                    <p className="hcm-resources-hint">From your instance (click to insert path):</p>
                  ) : (
                    <p className="hcm-resources-hint">Common resources (click to insert path). Your instance may support more.</p>
                  )}
                  <ul className="hcm-resources-list">
                    {hcmResources.map((r) => (
                      <li key={r.path}>
                        <button type="button" className="hcm-resource-item" onClick={() => handleSelectHcmResource(r.path)} title={r.title}>
                          <code>{r.path}</code>
                          {r.title && r.title !== r.path && <span className="hcm-resource-title"> â€” {r.title}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

QueryEditor.displayName = 'QueryEditor';

export default QueryEditor;


